import React, { useContext, useEffect, useState } from 'react';
import { navigate } from '@reach/router';

import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid'
import SendIcon from '@material-ui/icons/Send';
import Typography from '@material-ui/core/Typography';

import { all, timestamp } from '../../../app/data/validate';
import AssessmentServices from '../quotes/AssessmentServices';
import TravelExpenses from '../quotes/TravelExpenses';
import AuthContext from '../../../services/firebase/AuthContext';
import FireDataContext from '../../../services/firebase/FireDataContext';
import Spacer from '../../../app/common/ui/Spacer';
import ServiceInvoice from '../../billing/timesheets/ServiceInvoice';
import SubsistenceExpenses from '../quotes/SubsistenceExpenses';
import AccommodationExpenses from '../quotes/AccommodationExpenses';
import MemberMiniMenu from '../menus/MemberMiniMenu';
import FinanceDetails from './FinanceDetails';
import OrganizationAddressInput from './OrganizationAddressInput';
import { isRecipientAddressRequired } from '../invoices/InvoiceData';
import { onLocalHost } from '../../../app/common/useful';

const TimesheetEntryContent = (props) => {
    const { organization, timesheet, tariff } = props;
    return (
        <div>
            <FinanceDetails {...props} />
            {isRecipientAddressRequired({ tariff, items: timesheet.items(), organization }) && <OrganizationAddressInput organization={organization} />}
            <Spacer />
            <AssessmentServices {...props} />
            <TravelExpenses {...props} />
            <AccommodationExpenses {...props} />
            <SubsistenceExpenses {...props} />
        </div>
    );
}

const isSubmittable = (items) => {
    // the regex test is just a basic sense check to catch things like "InvalidDate"
    if (onLocalHost()) return true;

    const foo = items
        .filter(i => i.quantity && i.amount && i.key !== 'ADMIN01')
        .every(i => i.date && /^[0-9]*[/-][0-9]*[/-][0-9]*$/.test(i.date));

    console.log('items', items
        .filter(i => i.quantity && i.amount && (i.key !== 'ADMIN01'))
        .map(i => {
            return { item: i, ready: i.date && /^[0-9]*[/-][0-9]*[/-][0-9]*$/.test(i.date) }
        })
    );

    return foo;
}

const TimesheetEntryPage = ({ bookingId }) => {
    const db = useContext(FireDataContext);
    const { currentUser: { uid } } = useContext(AuthContext);
    const [timesheet, setTimesheet] = useState();
    const [tariff, setTariff] = useState();
    const [courtCaseNumber, setCourtCaseNumber] = useState();

    const [timesheetPath] = useState(`intermediaries/${uid}/timesheets/${bookingId}`);
    const [bookingPath] = useState(`intermediaries/${uid}/bookings/${bookingId}`);
    const [referral, setReferral] = useState();
    const [courtCase, setCourtCase] = useState();
    const [organization, setOrganization] = useState();
    const [submittable, setSubmittable] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [message, setMessage] = useState({ severity: 'info', title: `Notes`, text: `Keep a note of the work you've done here. If a cost has multiple dates - like a three night hotel stay - just enter the first date on the invoice. Once you're finished you can submit the invoice for payment` });

    //console.log(bookingPath, timesheetPath)

    useEffect(() => {
        (async () => {
            try {
                const booking = await db.get({
                    path: bookingPath
                });
                const ts = await db.get({
                    path: timesheetPath
                });

                const quote = await db.get({
                    path: `intermediaries/${uid}/quotes`,
                    query: {
                        field: 'booking.bookingId',
                        operator: '==',
                        value: bookingId
                    }
                });

                const court = Array.isArray(quote) && quote.length > 0 ? quote[0].court : {};

                var inv = undefined;
                if (ts && all(ts.tariff, ts.items, ts.bookingReference)) {
                    inv = new ServiceInvoice(ts.tariff, ts.items, ts.bookingReference);
                    inv.purchaseOrder = ts.purchaseOrder;

                    setSubmittable(isSubmittable(inv.items()));
                    setTariff(ts.tariff);
                    setSubmitted(ts.hasOwnProperty('submitted'))
                }
                else {
                    inv = new ServiceInvoice(booking.tariff, booking.items, booking.referral.bookingReference);
                    setSubmittable(isSubmittable(inv.items()));
                    setTariff(booking.tariff);
                }

                setReferral(booking.referral);

                const courtCasePath = `organizations/${booking.referral.organizationId}/cases/${booking.referral.courtCaseId}`;

                const cc = await db.get({ path: courtCasePath });

                setCourtCase(cc || { serviceUserName: '?', courtCaseNumber: '?' });
                inv.courtCaseNumber = ts?.courtCaseNumber || cc?.courtCaseNumber || '';
                setCourtCaseNumber(inv.courtCaseNumber);

                inv.court = ts?.court?.hasOwnProperty('costCentre') ? ts.court : court;

                setTimesheet(inv);
                setOrganization(await db.get({ path: `organizations/${booking.referral.organizationId}` }))

            }
            catch (error) {
                console.log(error);
            }
        })();

    }, [db, bookingPath, timesheetPath, courtCase?.courtCaseNumber, bookingId, uid]);

    const handleChange = () => {
        if (submitted) {
            setMessage({
                severity: 'warning',
                title: `Warning`,
                text: `This invoice has already been submitted for payment. Any changes that you make here will not be saved!`
            });

            return;
        }

        db.update({
            path: timesheetPath,
            payload: {
                bookingReference: referral.bookingReference,
                tariff,
                items: timesheet.items(),
                bookingPath,
                bookingId,
                status: 'draft',
                purchaseOrder: timesheet.purchaseOrder || '',
                courtCaseNumber: timesheet.courtCaseNumber || '',
                court: timesheet.court || {},
                invoicee: timesheet.invoicee || '',
                ts: timestamp()
            }
        })
            .catch(error => console.log(error));

        setCourtCaseNumber(timesheet.courtCaseNumber);
        setSubmittable(isSubmittable(timesheet.items()));
    }

    if (!all(timesheet, tariff, referral, courtCase, organization)) return <></>;

    return (
        <MemberMiniMenu>
            <Alert severity={message.severity} variant="filled"><AlertTitle>{message.title}</AlertTitle>{message.text}</Alert>
            <Spacer />
            <Grid container spacing={1}>
                <Grid item>
                    <Typography variant="h6" color="textSecondary">Invoice for Booking Reference {referral.bookingReference} / Court Case {courtCase.courtCaseNumber} / {courtCase.serviceUserName} </Typography>
                </Grid>
                <Grid item style={{ marginLeft: 'auto' }}>
                    <Button onClick={() => navigate(`/casework/invoices/${bookingId}`)} disabled={!submittable || submitted || !courtCaseNumber} variant="contained" color="secondary" endIcon={<SendIcon />}>Review Invoice PDF</Button>
                </Grid>
            </Grid>
            <Spacer />
            <TimesheetEntryContent timesheet={timesheet} tariff={tariff} onChange={handleChange} requireDates={true} organization={organization} referrer={referral.referrer} />
        </MemberMiniMenu>
    );
};

export default TimesheetEntryPage;